import React, { useContext, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useAuth0 } from '@auth0/auth0-react';
import Loader from '~components/loader';
import Markdown from 'markdown-to-jsx';
import CardSkeleton from '~components/card/skeleton';
import useScrollRestoration from '~hooks/useScrollRestoration';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import { isVerified } from '~helpers/common';

import NoResults from './no-results';
import ResultsItem from './results-item';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, Link } from 'react-router-dom';
import { UserContext } from '~contexts/user';
import { wishRoute } from '~helpers/wishRoute';
const SKELETON_COUNT = 6;

const ResultsList = ({
  matches = [],
  count,
  isCityPage = false,
  cityName = false,
  citySlug = false,
  locationId,
  isLoading,
  isLoadingMore,
  onShowMore,
  currentPage = 1,
  itemsPerPage = 20,
  newMatchesMode = false,
}) => {
  // Hold the restore until there are rows, so it cannot scroll onto a skeleton.
  useScrollRestoration({ canRestore: matches.length > 0 });
  const intl = useIntl();
  const { isAuthenticated } = useAuth0();
  const history = useHistory();
  const { user } = useContext(UserContext);
  const listRef = useRef(null);
  // Add a ref to track if we've ever had data
  const hasEverHadDataRef = useRef(matches && matches.length > 0);
  // Add state to store previous matches
  const [prevMatches, setPrevMatches] = useState(matches);
  const [isMobile, setIsMobile] = useState(false);
  // Pagination uses a count the server never renders; keeping it out of the first client
  // render keeps hydration aligned, or React grafts it onto the neighbouring div.
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalPages = Math.ceil(count / itemsPerPage);

  const [sentryRef] = useInfiniteScroll({
    loading: isLoadingMore,
    hasNextPage: isMobile && matches.length < count,
    onLoadMore: onShowMore,
    disabled:
      !isMobile ||
      isLoading ||
      isLoadingMore ||
      !onShowMore ||
      matches.length === 0 ||
      matches.length >= count,
    rootMargin: '0px 0px 600px 0px',
  });

  const getPageUrl = (page) => {
    const params = new URLSearchParams(window.location.search);
    if (page === 1) {
      params.delete('page');
    } else {
      params.set('page', page);
    }
    return `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const maxVisiblePages = isMobile ? 4 : 6;
    const pages = [];
    
    let startPage, endPage;

    if (totalPages <= maxVisiblePages) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const half = Math.floor(maxVisiblePages / 2);
      if (currentPage <= half) {
        startPage = 1;
        endPage = maxVisiblePages;
      } else if (currentPage + half >= totalPages) {
        startPage = totalPages - maxVisiblePages + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - half + (maxVisiblePages % 2 === 0 ? 1 : 0);
        endPage = currentPage + half;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="c-pagination">
        <Link
          to={getPageUrl(currentPage - 1)}
          className={cx('c-pagination__item c-pagination__item--prev', { 'is-disabled': currentPage === 1 })}
          onClick={(e) => currentPage === 1 && e.preventDefault()}
        >
          &laquo;
        </Link>

        {pages.map(page => (
          <Link
            key={page}
            to={getPageUrl(page)}
            className={cx('c-pagination__item', { 'is-active': page === currentPage })}
          >
            {page}
          </Link>
        ))}

        <Link
          to={getPageUrl(currentPage + 1)}
          className={cx('c-pagination__item c-pagination__item--next', { 'is-disabled': currentPage === totalPages })}
          onClick={(e) => currentPage === totalPages && e.preventDefault()}
        >
          &raquo;
        </Link>
      </div>
    );
  };

  // Reset state when matches become empty (city change)
  useEffect(() => {
    if (matches.length === 0) {
      setPrevMatches([]);
      hasEverHadDataRef.current = false;
    }
  }, [matches]);


  useEffect(() => {
    if (isLoading && listRef && listRef.current) {
      listRef.current.scrollTo(0, 0);
    }
  }, [isLoading]);

  // Update our tracking ref whenever matches has data
  useEffect(() => {
    if (matches && matches.length > 0) {
      hasEverHadDataRef.current = true;
      // Store current matches as previous matches when they're not empty
      setPrevMatches(matches);
    }
  }, [matches]);

  // Determine which matches to display
  let displayMatches = matches.length > 0 ? matches : (isLoading ? prevMatches : matches);

  if (!isAuthenticated && !newMatchesMode && displayMatches && displayMatches.length > 0) {
    // Create a shallow copy and filter out any existing 'login' items to prevent duplicates
    const cleanedMatches = displayMatches.filter((m) => m.id !== 'login');
    if (cleanedMatches.length <= 3) {
      displayMatches = [
        ...cleanedMatches,
        {
          id: 'login',
          name: 'Login',
        },
      ];
    } else {
      displayMatches = [...cleanedMatches];
      displayMatches.splice(3, 0, {
        id: 'login',
        name: 'Login',
      });
    }
  }
  // Check if we have matches to display, even if isLoading is true
  const hasServerRenderedData = displayMatches && displayMatches.length > 0;
  // Only show skeleton if we're loading, have no data to display, and have never had data before
  const shouldShowSkeleton = isLoading && !hasServerRenderedData && !hasEverHadDataRef.current;

  return (
    <div
      className={cx('c-matches-list c-matches-list--narrow', {
        'c-matches-list--no-results': displayMatches.length === 0,
      })}
    >
      <div className="c-matches-list__results" ref={listRef}>
        {shouldShowSkeleton ? (
          <CardSkeleton skeletonCount={SKELETON_COUNT} />
        ) : displayMatches && displayMatches.length > 0 ? (
          <>
            {displayMatches.map((match, index) => {
                if (match.id === 'login') {
                    return (
                    <Link
                      className="c-card c-card-login c-card-worker-invitation"
                      key={`worker-invitation-${index}`}
                      to="/intake/worker"
                      rel="nofollow"
                      aria-label={intl.formatMessage({ id: 'containers.workers.worker-invitation.aria-label', defaultMessage: 'Sign up to become a listed cleaner' })}
                    >
                      <div className="c-card__inner">
                        <div className="c-card__header-details">
                          <h3>{intl.formatMessage({ id: 'containers.workers.worker-invitation.title', defaultMessage: 'Be found by clients.' })}</h3>
                          <p>{intl.formatMessage({ id: 'containers.workers.worker-invitation.subtitle', defaultMessage: 'Sign up for free and find clients near you.' })}</p>
                        </div>
                        <div className="c-card__content">
                          <span className="c-btn c-card__button">
                            {intl.formatMessage({ id: 'containers.workers.worker-invitation.button', defaultMessage: 'Create your advertisement' })}
                          </span>
                        </div>
                      </div>
                    </Link>
                    )
                }
                return (
                    <ResultsItem
                    key={match.personId}
                    locationId={locationId}
                    personId={match.personId}
                    score={match.score}
                    price={match.price}
                    address={match.location}
                    experience={match.experience}
                    firstName={match.firstName}
                    profilePicture={match.profilePicture}
                    verification={isVerified(match.verification)}
                    lastLogin={match.lastLogin}
                    isOnline={match.isOnline}
                    personalNote={match.personalNote}
                    registerDate={match.registerDate}
                    days={match.days}
                    published={match.published}
                    isCityPage={isCityPage}
                    cityName={cityName}
                    citySlug={citySlug}
                    slug={match.slug}
                    showFavorite
                    />
                )
            })}
            {/* Remove the infinite scroll loader as we're using a button now */}
          </>
        ) : (
          <NoResults variant={newMatchesMode ? 'new-matches' : 'default'} />
        )}
      </div>

      {/* Pagination UI */}
      {isMounted && displayMatches && displayMatches.length > 0 && !isMobile && renderPagination()}

      {isMobile && matches.length < count && (
        <div className="c-infinite-scroll" ref={sentryRef} aria-hidden="true">
          {isLoadingMore && <Loader />}
        </div>
      )}

      {isCityPage && user?.accountType !== 'worker' && (
        <div className="c-more-desctiption">
          <div className="container">
            <h2>{intl.formatMessage({id: 'containers.workers.seo.city-footer.title'}, {city: cityName || ''})}</h2>
            <h3>{intl.formatMessage({id: 'containers.city.seo.cta.headline'}, {city: cityName || ''})}</h3>
            <p>{intl.formatMessage({id: 'containers.city.seo.free-service.sub-title'}, {city: cityName || ''})}</p>
            <Link className="c-btn" to={wishRoute({user,locationId})} rel="nofollow">{intl.formatMessage({id: 'containers.city.seo.cta.buttons.cleaner'}, {city: cityName || ''})}</Link>
          </div>
        </div>
      )}

    </div>
  );
};

export default ResultsList;
